import type { FormEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  createLoan,
  type LoanPayload,
} from "../../emprestimos/emprestimosApi";
import {
  PERIODICITY_OPTIONS,
  frequencyMonthsForLabel,
} from "./periodicity";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar empréstimos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o empréstimo.";
};

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
const inputStyle = {
  borderRadius: "var(--radius-button)",
  fontSize: "var(--text-base)",
} as const;
const labelClass = "block text-foreground mb-2";
const labelStyle = {
  fontSize: "var(--text-caption)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

export function NovoEmprestimoView() {
  const navigate = useNavigate();
  const [entidadeFinanceira, setEntidadeFinanceira] = useState("");
  const [descricao, setDescricao] = useState("");
  const [referenciaExtrato, setReferenciaExtrato] = useState("");
  const [periodicidade, setPeriodicidade] = useState("");
  const [estado, setEstado] = useState("");
  // Plano de amortização (opcional).
  const [capital, setCapital] = useState("");
  const [taxaAnual, setTaxaAnual] = useState("");
  const [numeroPrestacoes, setNumeroPrestacoes] = useState("");
  const [primeiraPrestacao, setPrimeiraPrestacao] = useState("");
  // Legacy (empréstimo simples, sem plano).
  const [prestacao, setPrestacao] = useState("");
  const [dataVencimento, setDataVencimento] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!entidadeFinanceira.trim()) {
      setSubmitError("A entidade financeira é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: LoanPayload = {
      financialEntity: entidadeFinanceira.trim(),
      description: descricao.trim() || null,
      bankReference: referenciaExtrato.trim() || null,
      periodicity: periodicidade.trim() || null,
      status: estado.trim() || null,
      principal: capital !== "" ? parseFloat(capital) : null,
      annualInterestRatePercent: taxaAnual !== "" ? parseFloat(taxaAnual) : null,
      numberOfInstallments:
        numeroPrestacoes !== "" ? parseInt(numeroPrestacoes, 10) : null,
      firstInstallmentDate: primeiraPrestacao !== "" ? primeiraPrestacao : null,
      frequencyMonths: frequencyMonthsForLabel(periodicidade),
      paidInstallmentsCount: 0,
      installmentValue: prestacao !== "" ? parseFloat(prestacao) : null,
      dueDate: dataVencimento !== "" ? dataVencimento : null,
    };

    try {
      await createLoan(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/emprestimos", {
      state: { toast: "Empréstimo criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/emprestimos")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-emprestimo-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Empréstimo"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Empréstimo"
        subtitle="Regista um novo empréstimo e define os seus dados."
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-emprestimo-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Empréstimo
                  </h3>

                  <div>
                    <label
                      htmlFor="entidadeFinanceira"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Entidade Financeira{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="entidadeFinanceira"
                      type="text"
                      placeholder="Insira a entidade financeira"
                      value={entidadeFinanceira}
                      onChange={(event) =>
                        setEntidadeFinanceira(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="descricao" className={labelClass} style={labelStyle}>
                      Descrição
                    </label>
                    <input
                      id="descricao"
                      type="text"
                      placeholder="Descrição do empréstimo"
                      value={descricao}
                      onChange={(event) => setDescricao(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="referenciaExtrato" className={labelClass} style={labelStyle}>
                      Referência no extrato
                    </label>
                    <input
                      id="referenciaExtrato"
                      type="text"
                      placeholder="Nº do contrato ou texto do débito no banco"
                      value={referenciaExtrato}
                      onChange={(event) => setReferenciaExtrato(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                    <p className="text-xs text-secondary-text mt-1">
                      Com isto, o débito de cada prestação fica pago sozinho na reconciliação bancária, com o valor real.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="periodicidade" className={labelClass} style={labelStyle}>
                      Periodicidade
                    </label>
                    <select
                      id="periodicidade"
                      value={periodicidade}
                      onChange={(event) => setPeriodicidade(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    >
                      <option value="">Selecionar…</option>
                      {PERIODICITY_OPTIONS.map((option) => (
                        <option key={option.label} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="estado" className={labelClass} style={labelStyle}>
                      Estado
                    </label>
                    <input
                      id="estado"
                      type="text"
                      placeholder="Ex: Ativo, Liquidado"
                      value={estado}
                      onChange={(event) => setEstado(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div>
                    <h3
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-section-title)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Plano de amortização
                    </h3>
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Preencha o capital, taxa e nº de prestações para calcular
                      automaticamente a prestação devida e o saldo em dívida
                      (método francês). Opcional.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="capital" className={labelClass} style={labelStyle}>
                      Capital inicial (€)
                    </label>
                    <input
                      id="capital"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={capital}
                      onChange={(event) => setCapital(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="taxaAnual" className={labelClass} style={labelStyle}>
                      Taxa anual TAN (%)
                    </label>
                    <input
                      id="taxaAnual"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Ex: 5.25"
                      value={taxaAnual}
                      onChange={(event) => setTaxaAnual(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="numeroPrestacoes"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Nº de prestações
                    </label>
                    <input
                      id="numeroPrestacoes"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Ex: 60"
                      value={numeroPrestacoes}
                      onChange={(event) =>
                        setNumeroPrestacoes(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="primeiraPrestacao"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Data da 1ª prestação
                    </label>
                    <input
                      id="primeiraPrestacao"
                      type="date"
                      value={primeiraPrestacao}
                      onChange={(event) =>
                        setPrimeiraPrestacao(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div>
                    <h3
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-section-title)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Empréstimo simples
                    </h3>
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Use estes campos para um empréstimo sem plano de
                      amortização (prestação e vencimento fixos).
                    </p>
                  </div>

                  <div>
                    <label htmlFor="prestacao" className={labelClass} style={labelStyle}>
                      Valor / Prestação (€)
                    </label>
                    <input
                      id="prestacao"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={prestacao}
                      onChange={(event) => setPrestacao(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="dataVencimento"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Data de Vencimento
                    </label>
                    <input
                      id="dataVencimento"
                      type="date"
                      value={dataVencimento}
                      onChange={(event) =>
                        setDataVencimento(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
